import React, { useState, useCallback } from "react";
import DataTable from "aldoo-ra/DataTable";
import Typography from "aldoo-ra/Typography";
import Button from "aldoo-ra/Button";
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { showNotification } from "aldoo-ra/Notification";
import { ArrowDownTrayIcon, EyeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { getInvoices } from "aldoo-ra/Store/api";
import Tooltip from "aldoo-ra/Tooltip";
import LoadingOverlay from "aldoo-ra/LoadingOverlay";

const InvoiceDetailsModal = ({
  invoice,
  onClose,
  getPaymentMethodInfo,
  onDownload,
  texts,
}) => {
  if (!invoice) return null;

  // Mobile view invoice details modal:
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-paper rounded-[24px] w-full max-w-lg max-h-[90vh] overflow-y-auto p-8">
        <div className="pb-7 border-b flex justify-between items-center">
          <Typography
            variant="h4"
            className="my-0"
          >
              {texts?.mobile_dialog_title || "Invoice Details"}
            </Typography>

          <Button variant="text" onClick={onClose}>
            <XMarkIcon className="w-6 h-6" />
          </Button>
        </div>

        <div className=" font-sm pt-4 space-y-6">
          <div className="">
            <Typography
              className="text-gray_1 mb-2 mr-4 lg:mr-0"
            >
              {texts?.document || "Document #"}
            </Typography>

            <Typography>{invoice.number}</Typography>
          </div>

          <div>
            <Typography className="text-gray_1 mb-2 mr-4 lg:mr-0">
              {texts?.date || "Date"}
            </Typography>

            <Typography>
              {moment(invoice.created * 1000).format("MMM D, YYYY")}
            </Typography>
          </div>

          <div>
            <Typography className="text-gray_1 mb-2 mr-4 lg:mr-0">
              {texts?.price || "Price"}
            </Typography>

            <Typography>
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: invoice.currency,
              }).format(invoice.amount_paid / 100)}
            </Typography>
          </div>

          <div>
            <Typography className="text-gray_1 mb-2 mr-4 lg:mr-0">
              {texts?.payment_method || "Payment Method"}
            </Typography>

            <Typography>{getPaymentMethodInfo(invoice)}</Typography>
          </div>

          <div>
            <Typography className="text-gray_1 mb-2 mr-4 lg:mr-0">
              {texts?.status || "Status"}
            </Typography>

            <Typography
              className={`rounded-full px-3 py-1 my-0 ${
                invoice.status === "paid"
                  ? "bg-success/10 text-success"
                  : "bg-progress/10 text-progress"
              }`}
            >
              {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
            </Typography>
          </div>

          <div>
            <Typography className="text-gray_1 mb-2 mr-4 lg:mr-0">
              {texts?.download || "Download"}
            </Typography>
            <Button
              variant="text"
              onClick={() => onDownload(invoice)}
              disabled={!invoice.invoice_pdf}
              className="p-0"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function InvoiceBrowser() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1080);

  const texts = TextBundle({ id: "invoices" })

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1080);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDownload = async (invoice) => {
    try {
      setOverlayLoading(true)
      window.open(invoice.invoice_pdf, "_blank")
    } catch (error) {
      showNotification({
        message: texts?.download_error || "Error downloading invoice",
        className: "bg-error text-white",
      })
    }

    setOverlayLoading(false)
  };

  const handleView = async (invoice) => {
    if (isMobile) return setSelectedInvoice(invoice)

    try {
      setOverlayLoading(true)
      window.open(invoice.hosted_invoice_url, "_blank")
    } catch (error) {
      showNotification({
        message: texts?.view_error || "Error viewing invoice",
        className: "bg-error text-white",
      })
    }

    setOverlayLoading(false)
  };

  const getPaymentMethodInfo = useCallback((invoice) => {
    const paymentMethod = invoice.payment_intent?.payment_method;
    if (!paymentMethod) {
      return "N/A";
    }

    if (invoice.payment_method_display) {
      return invoice.payment_method_display;
    }

    if (paymentMethod.type === "card" && paymentMethod.card) {
      return `${paymentMethod.card.brand?.toUpperCase()} **** ${
        paymentMethod.card.last4
      }`;
    }

    const methodDetails = paymentMethod[paymentMethod.type];
    if (!methodDetails) return paymentMethod.type?.toUpperCase() || "N/A";

    const displayParts = [paymentMethod.type.toUpperCase()];

    if (methodDetails.email) {
      displayParts.push(`: ${methodDetails.email}`);
    } else if (methodDetails.last4) {
      displayParts.push(` **** ${methodDetails.last4}`);
    } else if (methodDetails.bank_name) {
      displayParts.push(`: ${methodDetails.bank_name}`);
    }

    return displayParts.join("");
  }, []);

  const mobileRenders = [
    {
      id: "created",
      title: texts?.date || "Date",
      headerClassName: "w-[120px]",
      render: (value, item) => (
        <Typography>
          {moment(item.created * 1000).format("MMM D, YYYY")}
        </Typography>
      ),
    },
    {
      id: "amount_paid",
      title: texts?.price || "Price",
      headerClassName: "w-[90px]",
      render: (value, item) => (
        <Typography>
          {new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: item.currency,
          }).format(item.amount_paid / 100)}
        </Typography>
      ),
    },
    {
      id: "view_more",
      title: texts?.view_more || "View More",
      headerClassName: "w-[100px] text-center",
      render: (value, item) => (
        <div className="flex items-center justify-center">
          <Button
            variant="text"
            onClick={() => handleView(item)}
            disabled={overlayLoading}
          >
            <EyeIcon className="w-5 h-5 items-center" />
          </Button>
        </div>
      ),
    },
  ];

  const desktopRenders = [
    {
      id: "created",
      title: texts?.date || "Date",
      headerClassName: "w-[130px]",
      render: (value, item) => (
        <Typography>
          {moment(item.created * 1000).format("MMM D, YYYY")}
        </Typography>
      ),
    },
    {
      id: "amount_paid",
      title: texts?.price || "Price",
      headerClassName: "w-[100px]",
      render: (value, item) => (
        <Typography>
          {new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: item.currency,
          }).format(item.amount_paid / 100)}
        </Typography>
      ),
    },
    {
      id: "payment_method",
      title: texts?.payment_method || "Payment Method",
      headerClassName: "w-[200px]",
      render: (value, item) => (
        <Typography>
          {getPaymentMethodInfo(item)}
        </Typography>
      ),
    },
    {
      id: "status",
      title: texts?.status || "Status",
      headerClassName: "w-[100px]",
      render: (value, item) => (
        <Typography>
          <span
            className={`px-3 py-1 rounded-full ${
              item.status === "paid"
                ? "bg-success/10 text-success"
                : "bg-progress/10 text-progress"
            }`}
          >
            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
          </span>
        </Typography>
      ),
    },
    {
      id: "actions",
      title: texts?.actions || "Actions",
      headerClassName: "w-[80px]",
      render: (value, item) => (
        <div className="flex items-center space-x-5">
          <Tooltip content="Preview">
            <Button
              variant="text"
              onClick={() => handleView(item)}
              disabled={!item.hosted_invoice_url || overlayLoading}
            >
              <EyeIcon className="w-5 h-5 items-center" />
            </Button>
          </Tooltip>

          <Tooltip content="Download">
            <Button
              variant="text"
              onClick={() => handleDownload(item)}
              disabled={!item.invoice_pdf || overlayLoading}
            >
              <ArrowDownTrayIcon className="w-5 h-5 items-center" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full max-w-[554px] lg:max-w-full bg-paper rounded-[24px] shadow-custom_card px-4 xl:px-6 py-9 mx-auto lg:mx-0">
      <DataTable
        variant="aqua_cards"
        //title={texts?.title || "Billing History"}
        preferredHeight="60vh"
        apiMethod={getInvoices}
        itemHeight="h-[50px]"
        headerControls={null}
        searchObject={{}}
        requestFields={{}}
        sort={{ created: -1 }}
        renders={isMobile ? mobileRenders : desktopRenders}
        refreshKey={refreshKey}
        showFooter={true}
        apiDataField="data"
        minSearchTriggerTime={500}
        localUserPreferencesKey="account.invoices"
        onDataLoadingElement={
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        }
        noData={
          <div className="flex justify-center items-center min-h-[200px]">
            <Typography variant="body_18">
              {texts?.no_invoices || "No invoices found"}
            </Typography>
          </div>
        }
      />

      {selectedInvoice && (
        <InvoiceDetailsModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          getPaymentMethodInfo={getPaymentMethodInfo}
          onDownload={(invoice) => handleDownload(invoice, texts)}
          texts={texts}
        />
      )}

      <LoadingOverlay
        title={texts?.loading_title || "Please wait..."}
        message={texts?.loading_message || "Processing your request"}
        loading={overlayLoading}
      />
    </div>
  );
}